import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Building2, Compass, Target, Users } from "lucide-react";
import { api } from "@/lib/api";

export const TopEmployers = () => {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/public/top-employers").then((r) => setD(r.data)).catch(() => setD(false)); }, []);
  if (!d || !d.employers.length) return null;
  return (
    <section className="border-t bg-white py-20" data-testid="top-employers-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-8">
        <p className="eyebrow mb-3">Open roles from top employers</p>
        <h2 className="max-w-2xl text-3xl font-bold">Browse current openings straight from the career boards of leading employers.</h2>
        <div className="mt-10 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          {d.employers.map((e, i) => (
            <Link key={e.name} to="/register" className="lift group rounded-xl border bg-background p-4 fade-up" style={{ animationDelay: `${i * 40}ms` }}
              data-testid={`top-employer-${e.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
              <div className="flex items-center gap-2"><Building2 className="h-4 w-4 shrink-0 text-primary" /><span className="truncate font-semibold">{e.name}</span></div>
              <p className="mt-2 font-mono text-xs text-slate-500">{e.open_roles.toLocaleString()} open roles{e.us_roles ? ` · ${e.us_roles.toLocaleString()} in the US` : ""}</p>
              <p className="mt-1 truncate text-xs text-slate-400">{e.industry}</p>
            </Link>
          ))}
        </div>
        <p className="mt-6 max-w-3xl text-xs text-slate-500" data-testid="top-employers-disclaimer">
          Role counts are live from each employer's public careers board and refresh several times a day. Listing an employer's public jobs doesn't mean the employer partners with or endorses JobPedestal.
        </p>
      </div>
    </section>
  );
};

const SCENARIOS = [
  { icon: Compass, who: "VP of Finance, weighing two offers", slug: "vp-finance",
    steps: ["Imports her resume and sets a compensation floor and remote preference.", "Match Analyst breaks each role's fit down factor by factor, so the trade-offs are explicit.", "Salary Negotiator drafts talking points from the offers she enters, with nothing invented."],
    outcome: "She walks into negotiations with a clear, documented comparison of both offers." },
  { icon: Target, who: "Director of Engineering, moving to a growth-stage company", slug: "director-engineering",
    steps: ["Filters verified, employer-direct openings by seniority, salary disclosure and freshness.", "Resume Optimizer tailors his record to each role and flags gaps for him to fill rather than fabricating them.", "Application tracking keeps confirmed, reported and unconfirmed statuses separate."],
    outcome: "He spends his time on roles that are actually open and actually fit." },
  { icon: Users, who: "Talent lead, hiring a Head of Sales", slug: "talent-lead",
    steps: ["Posts the role on a recruiter plan with a verified application destination.", "Searches only candidates who have opted in to be visible to recruiters.", "Reaches out through the platform; candidates control what they share."],
    outcome: "A consent-first shortlist with no scraped or cold-sourced profiles." },
];

export const ExampleScenarios = () => (
  <section className="mx-auto max-w-7xl px-4 py-24 sm:px-8" data-testid="example-scenarios-section">
    <p className="eyebrow mb-3">How it works in practice</p>
    <h2 className="max-w-2xl text-3xl font-bold">Example scenarios</h2>
    <p className="mt-3 max-w-2xl text-sm text-slate-600">Illustrative examples of how people use JobPedestal. These aren't real customers, and they include no outcome statistics.</p>
    <div className="mt-10 grid gap-5 lg:grid-cols-3">
      {SCENARIOS.map(({ icon: Icon, who, slug, steps, outcome }) => (
        <article key={slug} className="lift flex flex-col rounded-2xl border bg-white p-6" data-testid={`scenario-${slug}`}>
          <span className="w-fit rounded-full bg-amber-50 px-2.5 py-0.5 text-[11px] font-medium uppercase tracking-wide text-amber-800">Example scenario</span>
          <Icon className="mt-5 h-6 w-6 text-primary" />
          <h3 className="mt-3 text-lg font-semibold">{who}</h3>
          <ol className="mb-6 mt-4 space-y-2 text-sm text-slate-600">{steps.map((s, i) => <li key={s} className="flex gap-2"><span className="font-mono text-xs text-slate-400">0{i + 1}</span>{s}</li>)}</ol>
          <p className="mt-auto border-t pt-4 text-sm font-medium">{outcome}</p>
        </article>
      ))}
    </div>
    <Link to="/register" className="mt-8 inline-flex items-center text-sm font-semibold text-primary hover:underline" data-testid="scenarios-cta-link">Start free <ArrowRight className="ml-1 h-4 w-4" /></Link>
  </section>
);
